import { useCopy } from '../../content'

/**
 * Plan view of a garden split into three irrigation zones, beside the
 * irrigation section's intro: the house (hip roof seen from above), the lawn
 * with its pop-up sprinklers, the beds and the hedge on drip line, all fed
 * from one tap. The soil-moisture sensors are drawn dashed: they arrive with
 * the smart layer (early 2027).
 *
 * Motion is the zone map's water (`motion/useDrawPath.ts`, `spreadLikeWater`)
 * on the same markup contract: the pipes are revealed through `FLOW_MASK_ID`,
 * whose white `path[data-map-flow]` copies of every route start at the tap;
 * `path[data-map-route]` (unstroked) carry the same `d` for measuring; a
 * blurred `path[data-map-glow]` rides each front; each sprinkler and drip
 * emitter is a `g[data-map-dot]` that pops, with its `[data-map-ripple]`, as
 * the water reaches it. A sprinkler's dot group also holds its throw circle,
 * so the spray grows out of the head; the lawn clip sits on an ancestor, in
 * untransformed user space. In the markup everything is drawn and the glows
 * and ripples are at opacity 0, so without motion the plan is complete.
 *
 * All coordinates are integers in a fixed 480 × 360 frame, so the
 * prerendered SVG and the first client render are identical. No text inside
 * the drawing: the legend underneath is HTML, legible at phone width.
 */

const W = 480
const H = 360
const FLOW_MASK_ID = 'vv2-plan-flow'
const FLOW_BLUR_ID = 'vv2-plan-flow-blur'
const LAWN_CLIP_ID = 'vv2-plan-lawn'
const MOW_ID = 'vv2-plan-mow'

/** Water inks, as on the zone map: the pipe (dark enough on paper), and the glow and ripples. */
const WATER = 'stroke-[#4f7d96] dark:stroke-vv2-glacier'
const WATER_FILL = 'fill-[#4f7d96] dark:fill-vv2-glacier'
const WATER_LINE = 'stroke-[#7ea7bf] dark:stroke-[#5f8ca3]'

const TAP = { x: 180, y: 126 }
const LAWN = { x: 20, y: 140, w: 380, h: 200 }

/** Every route the water runs, each from the tap outwards: two lawn rows (west, east), the beds, the hedge (north, south). */
const ROUTES = [
  'M180 126V190H80',
  'M180 126V190H340',
  'M180 126V292H80',
  'M180 126V292H340',
  'M180 126H204V66H388',
  'M180 126H412V28',
  'M180 126H412V332',
] as const
/** The pipes as drawn: supply lines solid, drip lines dotted. */
const SUPPLY = ['M180 126V292', 'M80 190H340', 'M80 292H340', 'M180 126H412', 'M204 126V66'] as const
const DRIP = ['M204 66H388', 'M412 28V332'] as const

const SPRINKLERS: readonly [number, number][] = [
  [80, 190], [210, 190], [340, 190],
  [80, 292], [210, 292], [340, 292],
]
/** Drip emitters: one per shrub on the bed line, then along the hedge (none at the junction). */
const EMITTERS: readonly [number, number][] = [
  [236, 66], [274, 66], [312, 66], [350, 66], [384, 66],
  [412, 40], [412, 84], [412, 170], [412, 214], [412, 258], [412, 302],
]
const SHRUBS: readonly [number, number, number][] = [
  [236, 40, 12], [274, 40, 11], [312, 40, 13], [350, 40, 11], [384, 40, 9],
  [248, 92, 11], [290, 92, 12], [332, 92, 10], [364, 92, 11],
]
/** One sensor per zone: lawn, beds, hedge. */
const SENSORS: readonly [number, number][] = [
  [262, 241], [386, 94], [440, 150],
]
/** The hedge as a row of overlapping crowns, nudged off a straight line. */
const HEDGE: readonly [number, number][] = Array.from({ length: 13 }, (_, i): [number, number] => [i % 2 ? 441 : 439, 34 + i * 24])

const PING =
  '[transform-box:fill-box] [transform-origin:center] motion-safe:animate-[ping_2.6s_cubic-bezier(0,0,0.2,1)_infinite]'

/** 22 × 14 legend swatches. */
const SWATCH = { width: 22, height: 14, viewBox: '0 0 22 14', 'aria-hidden': true, className: 'shrink-0' } as const

export function GardenPlan() {
  const t = useCopy().irrigation.plan
  const l = t.legend
  return (
    <div className="overflow-hidden rounded-2xl border border-vv2-hairline bg-vv2-paper p-4 sm:p-6">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-labelledby="vv2-plan-title vv2-plan-desc"
        className="block h-auto w-full"
      >
        <title id="vv2-plan-title">{t.title}</title>
        <desc id="vv2-plan-desc">{t.desc}</desc>
        <defs>
          <pattern id={MOW_ID} patternUnits="userSpaceOnUse" width={40} height={H}>
            <rect width={20} height={H} className="fill-vv2-paper/25" />
          </pattern>
          <clipPath id={LAWN_CLIP_ID}>
            <rect x={LAWN.x} y={LAWN.y} width={LAWN.w} height={LAWN.h} rx={10} />
          </clipPath>
          <mask id={FLOW_MASK_ID} maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
            <g fill="none" stroke="#fff" strokeWidth={14} strokeLinecap="round" strokeLinejoin="round">
              {ROUTES.map((d) => (
                <path key={d} data-map-flow d={d} />
              ))}
            </g>
          </mask>
          <filter id={FLOW_BLUR_ID} filterUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
            <feGaussianBlur stdDeviation={3} />
          </filter>
        </defs>

        {/* Lawn, with mowing stripes */}
        <rect x={LAWN.x} y={LAWN.y} width={LAWN.w} height={LAWN.h} rx={10} className="fill-vv2-moss/30" />
        <rect x={LAWN.x} y={LAWN.y} width={LAWN.w} height={LAWN.h} rx={10} fill={`url(#${MOW_ID})`} />

        {/* Beds and their shrubs */}
        <rect x={192} y={20} width={208} height={92} rx={14} className="fill-vv2-ink/10" />
        {SHRUBS.map(([x, y, r]) => (
          <circle key={`${x} ${y}`} cx={x} cy={y} r={r} className="fill-vv2-moss/50 stroke-vv2-forest/30" strokeWidth={1} />
        ))}

        {/* Hedge */}
        {HEDGE.map(([x, y]) => (
          <circle key={y} cx={x} cy={y} r={15} className="fill-vv2-forest/45 stroke-vv2-paper/60" strokeWidth={1} />
        ))}

        {/* House, hip roof from above: lit from the top left, so the bottom and right planes are shaded */}
        <g className="stroke-vv2-ink/30" strokeWidth={1.2} strokeLinejoin="round">
          <path d="M20 20H170L124 66H66Z" className="fill-vv2-ink/5" />
          <path d="M20 20L66 66L20 112Z" className="fill-vv2-ink/5" />
          <path d="M20 112H170L124 66H66Z" className="fill-vv2-ink/15" />
          <path d="M170 20L124 66L170 112Z" className="fill-vv2-ink/15" />
          <rect x={128} y={30} width={12} height={12} rx={1} className="fill-vv2-ink/20" />
        </g>

        {/* Pipes, revealed through the flow mask (see motion above) */}
        <g mask={`url(#${FLOW_MASK_ID})`} fill="none" strokeLinecap="round" strokeLinejoin="round">
          {SUPPLY.map((d) => (
            <path key={d} d={d} className={WATER} strokeWidth={3} />
          ))}
          {DRIP.map((d) => (
            <path key={d} d={d} className={WATER} strokeWidth={2.2} strokeDasharray="1 5" />
          ))}
        </g>
        <g fill="none" stroke="none">
          {ROUTES.map((d) => (
            <path key={d} data-map-route d={d} />
          ))}
        </g>
        <g filter={`url(#${FLOW_BLUR_ID})`} fill="none" strokeLinecap="round" className={WATER_LINE}>
          {ROUTES.map((d) => (
            <path key={d} data-map-glow d={d} strokeWidth={8} opacity={0} />
          ))}
        </g>

        {/* Sprinklers: head and throw, clipped to the lawn */}
        <g clipPath={`url(#${LAWN_CLIP_ID})`}>
          {SPRINKLERS.map(([x, y]) => (
            <g key={`${x} ${y}`}>
              <circle data-map-ripple cx={x} cy={y} r={4.5} fill="none" className={WATER_LINE} strokeWidth={1.4} opacity={0} />
              <g data-map-dot transform={`translate(${x} ${y})`}>
                <circle r={64} className="fill-vv2-glacier/10 stroke-vv2-glacier" strokeWidth={1} strokeDasharray="3 4" />
                <circle r={4.5} className={`${WATER_FILL} stroke-vv2-paper`} strokeWidth={1.5} />
              </g>
            </g>
          ))}
        </g>

        {/* Drip emitters */}
        {EMITTERS.map(([x, y]) => (
          <g key={`${x} ${y}`}>
            <circle data-map-ripple cx={x} cy={y} r={2.5} fill="none" className={WATER_LINE} strokeWidth={1.2} opacity={0} />
            <g data-map-dot transform={`translate(${x} ${y})`}>
              <circle r={2.5} className={WATER_FILL} />
            </g>
          </g>
        ))}

        {/* Soil-moisture sensors: coming with the smart layer, so dashed */}
        {SENSORS.map(([x, y]) => (
          <g key={`${x} ${y}`} transform={`translate(${x} ${y})`}>
            <circle r={7} className="fill-vv2-paper stroke-vv2-ink-soft" strokeWidth={1.2} strokeDasharray="2.5 2.5" />
            <path d="M0 -3V3" className="stroke-vv2-ink-soft" strokeWidth={1.2} strokeLinecap="round" />
          </g>
        ))}

        {/* The tap */}
        <circle cx={TAP.x} cy={TAP.y} r={11} fill="none" className={`${WATER} ${PING}`} strokeWidth={1.5} />
        <circle cx={TAP.x} cy={TAP.y} r={7} className={`fill-vv2-paper ${WATER}`} strokeWidth={2.2} />
        <circle cx={TAP.x} cy={TAP.y} r={2.5} className={WATER_FILL} />
      </svg>

      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-vv2-ink-soft">
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <rect x={1} y={2} width={20} height={10} rx={3} className="fill-vv2-moss/30" />
          </svg>
          {l.lawn}
        </li>
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <rect x={1} y={2} width={20} height={10} rx={3} className="fill-vv2-ink/10" />
            <circle cx={11} cy={7} r={3.5} className="fill-vv2-moss/50" />
          </svg>
          {l.beds}
        </li>
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <circle cx={7} cy={7} r={5.5} className="fill-vv2-forest/45" />
            <circle cx={15} cy={7} r={5.5} className="fill-vv2-forest/45" />
          </svg>
          {l.hedge}
        </li>
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <circle cx={11} cy={7} r={5} className={`fill-vv2-paper ${WATER}`} strokeWidth={2} />
            <circle cx={11} cy={7} r={1.8} className={WATER_FILL} />
          </svg>
          {l.water}
        </li>
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <circle cx={11} cy={7} r={6} className="fill-vv2-glacier/10 stroke-vv2-glacier" strokeWidth={1} strokeDasharray="2 2" />
            <circle cx={11} cy={7} r={2.8} className={WATER_FILL} />
          </svg>
          {l.sprinkler}
        </li>
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <path d="M2 7H20" fill="none" className={WATER} strokeWidth={2.2} strokeDasharray="1 5" strokeLinecap="round" />
          </svg>
          {l.drip}
        </li>
        <li className="flex items-center gap-2">
          <svg {...SWATCH}>
            <circle cx={11} cy={7} r={5.5} className="fill-vv2-paper stroke-vv2-ink-soft" strokeWidth={1.2} strokeDasharray="2 2" />
            <path d="M11 5V9" className="stroke-vv2-ink-soft" strokeWidth={1.2} strokeLinecap="round" />
          </svg>
          {l.sensor}
        </li>
      </ul>
    </div>
  )
}
